import { useQueryClient } from '@tanstack/react-query';
import { Award, Check } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { prayerLabel } from '@/shared/lib/labels';
import { Async } from '@/shared/ui/Async';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { CompensationBadge } from '@/shared/ui/CompensationBadge';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { Stars } from '@/shared/ui/Stars';
import { ApplicationStatusChip, CoverageStatusChip } from '@/shared/ui/StatusChip';
import { TextArea } from '@/shared/ui/TextArea';
import { useSessionStore } from '@/stores/session.store';
import { useUiStore } from '@/stores/ui.store';

import { ChatPanel } from '@/features/chat';
import { useAppSettings } from '@/features/config/hooks/use-config';
import { RatingSection } from '@/features/ratings/components/RatingSection';
import { useCanRateAsManager } from '@/features/ratings/hooks/use-ratings';
import { useTicketByApplication } from '@/features/support';

import { CheckInPanel } from '../components/CheckInPanel';
import { coverageDatesText } from '../components/coverage-format';
import {
  coverageKeys,
  useAcceptApplication,
  useApply,
  useAutoSyncPrayerTimes,
  useCancelRequest,
  useCandidates,
  useCompleteApplication,
  useMyApplicationForRequest,
  useRequest,
  useRequestApplications,
  useWithdrawApplication,
} from '../hooks/use-coverage';
import type { CoverageApplication } from '../model/coverage-application';
import type { CoverageRequest } from '../model/coverage-request';

export function RequestDetailRoute(): JSX.Element {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const userId = useSessionStore((s) => s.userId);
  const query = useRequest(id);

  return (
    <main className="page stack">
      <h1>{t('coverage.detailTitle')}</h1>
      <Async query={query}>
        {(request) => (
          <>
            <RequestHeader request={request} />
            {request.createdBy === userId ? (
              <OwnerSection request={request} />
            ) : (
              <ProviderSection request={request} />
            )}
          </>
        )}
      </Async>
    </main>
  );
}

function RequestHeader({ request }: { request: CoverageRequest }): JSX.Element {
  const { t } = useTranslation();
  const locale = useUiStore((s) => s.locale);
  const { flag } = useAppSettings();
  return (
    <section className="stack">
      <div className="row row--between">
        <h2>{request.masjidName ?? t('masjid.fallbackName')}</h2>
        <CoverageStatusChip status={request.status} />
      </div>
      {request.masjidCity ? <p className="muted">{request.masjidCity}</p> : null}
      <p>{coverageDatesText(locale, request)}</p>
      <div className="wrap">
        {request.prayerSlots.map((p) => (
          <span
            key={p}
            className={`badge${p === 'jumuah' ? ' badge--jumuah' : ''}`}
            style={p === 'jumuah' ? undefined : { background: 'var(--color-surface-2)' }}
          >
            {prayerLabel(t, p)}
          </span>
        ))}
      </div>
      {flag('feature.compensation') ? <CompensationBadge value={request.compensation} /> : null}
      {request.notes ? <p>{request.notes}</p> : null}
    </section>
  );
}

function ProviderSection({ request }: { request: CoverageRequest }): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const userId = useSessionStore((s) => s.userId);
  const myApplication = useMyApplicationForRequest(request.id);
  const apply = useApply(request.id);
  const withdraw = useWithdrawApplication();
  const [message, setMessage] = useState('');
  const { flag } = useAppSettings();

  return (
    <Async query={myApplication}>
      {(application) => {
        if (application) {
          return (
            <section className="stack">
              <div className="row">
                <span>{t('application.status')}:</span>
                <ApplicationStatusChip status={application.status} />
              </div>
              {application.message ? <p>{application.message}</p> : null}
              {application.status === 'pending' ? (
                <Button
                  variant="outline"
                  loading={withdraw.isPending}
                  onClick={() =>
                    withdraw.mutate(application.id, {
                      onSuccess: () =>
                        queryClient.invalidateQueries({
                          queryKey: coverageKeys.myApplication(request.id, userId ?? 'anon'),
                        }),
                    })
                  }
                >
                  {t('application.withdraw')}
                </Button>
              ) : null}
              {application.status === 'accepted' && flag('feature.gps_checkin') ? (
                <CheckInPanel application={application} />
              ) : null}
              {['accepted', 'completed', 'no_show'].includes(application.status) &&
              flag('feature.chat') ? (
                <ChatPanel applicationId={application.id} />
              ) : null}
              {application.status === 'completed' && flag('feature.ratings') ? (
                <RatingSection applicationId={application.id} rateeId={request.createdBy} />
              ) : null}
              {application.status === 'no_show' ? (
                <DisputeTicketLink applicationId={application.id} />
              ) : null}
            </section>
          );
        }

        if (request.status !== 'open') {
          return <p className="muted">{t('coverage.notOpen')}</p>;
        }

        return (
          <section className="stack">
            <h3>{t('application.applyTitle')}</h3>
            <TextArea
              label={t('application.messageOptional')}
              placeholder={t('application.messageHint')}
              value={message}
              maxLength={1000}
              onChange={(e) => setMessage(e.target.value)}
            />
            <Button
              loading={apply.isPending}
              onClick={() => apply.mutate(message.trim().length === 0 ? null : message.trim())}
            >
              {t('application.submit')}
            </Button>
          </section>
        );
      }}
    </Async>
  );
}

function OwnerSection({ request }: { request: CoverageRequest }): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isOpen = request.status === 'open';
  const applications = useRequestApplications(request.id, true);
  const accept = useAcceptApplication(request.id);
  const cancel = useCancelRequest(request.id);
  const [confirmAcceptId, setConfirmAcceptId] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  return (
    <section className="stack">
      <div className="row row--between">
        <h3>
          {t('application.applicants', {
            count: applications.data?.length ?? 0,
          })}
        </h3>
        {isOpen ? (
          <Button variant="danger" onClick={() => setConfirmCancel(true)}>
            {t('coverage.cancel')}
          </Button>
        ) : null}
      </div>

      <Async
        query={applications}
        isEmpty={(l) => l.length === 0}
        emptyMessage={t('application.noneYet')}
      >
        {(list) => (
          <div className="stack">
            {list.map((application) => (
              <div key={application.id} className="card stack">
                <div className="row row--between">
                  <strong>{application.providerName ?? t('application.providerFallback')}</strong>
                  <ApplicationStatusChip status={application.status} />
                </div>
                {application.providerCity ? (
                  <span className="muted">{application.providerCity}</span>
                ) : null}
                {application.message ? <p>{application.message}</p> : null}
                {isOpen && application.status === 'pending' ? (
                  <Button onClick={() => setConfirmAcceptId(application.id)}>
                    {t('application.accept')}
                  </Button>
                ) : null}
              </div>
            ))}
            {!isOpen ? <AcceptedManagement request={request} applications={list} /> : null}
          </div>
        )}
      </Async>

      <ConfirmDialog
        open={confirmAcceptId !== null}
        body={t('application.acceptConfirmBody')}
        confirmLabel={t('application.accept')}
        loading={accept.isPending}
        onCancel={() => setConfirmAcceptId(null)}
        onConfirm={() =>
          accept.mutate(confirmAcceptId as string, { onSuccess: () => setConfirmAcceptId(null) })
        }
      />
      <ConfirmDialog
        open={confirmCancel}
        body={t('coverage.cancelConfirmBody')}
        confirmLabel={t('coverage.cancel')}
        loading={cancel.isPending}
        danger
        onCancel={() => setConfirmCancel(false)}
        onConfirm={() => cancel.mutate(undefined, { onSuccess: () => navigate('/requests') })}
      />

      {isOpen ? <CandidatesSection request={request} /> : null}
    </section>
  );
}

/** Phase 3: the requester manages the accepted coverage — attendance evidence,
 * marking completed / no-show, and rating the provider afterwards. */
function AcceptedManagement({
  request,
  applications,
}: {
  request: CoverageRequest;
  applications: readonly CoverageApplication[];
}): JSX.Element | null {
  const { t } = useTranslation();
  const complete = useCompleteApplication(request.id);
  const [confirmNoShow, setConfirmNoShow] = useState(false);
  const { flag } = useAppSettings();

  // Fixes "prayer times not synced": populate the check-in verification cache for
  // this request's masjid + dates as soon as the owner views the accepted coverage.
  useAutoSyncPrayerTimes(
    request.masjidId,
    request.masjidLat,
    request.masjidLng,
    request.startDate,
    request.endDate,
  );

  const application = applications.find(
    (a) => a.status === 'accepted' || a.status === 'completed' || a.status === 'no_show',
  );
  if (!application) return null;

  return (
    <section className="card stack">
      <h3>{t('coverage.acceptedCoverage')}</h3>

      <div className="wrap">
        {application.checkedInAt ? (
          <Badge
            label={
              application.checkinVerified ? t('checkin.verifiedIn') : t('checkin.unverifiedIn')
            }
            color={
              application.checkinVerified ? 'var(--color-success)' : 'var(--color-on-surface-muted)'
            }
          />
        ) : (
          <Badge label={t('checkin.notCheckedIn')} color="var(--color-on-surface-muted)" />
        )}
        {application.needsReview ? (
          <Badge label={t('review.flagged')} color="var(--color-danger)" />
        ) : null}
      </div>

      {application.status === 'accepted' ? (
        <div className="row">
          <Button
            loading={complete.isPending}
            onClick={() => complete.mutate({ applicationId: application.id, outcome: 'completed' })}
          >
            {t('coverage.markCompleted')}
          </Button>
          <Button variant="danger" onClick={() => setConfirmNoShow(true)}>
            {t('coverage.markNoShow')}
          </Button>
        </div>
      ) : null}

      {flag('feature.chat') ? <ChatPanel applicationId={application.id} /> : null}

      {application.status === 'completed' && flag('feature.ratings') ? (
        <ManagerGatedRating application={application} />
      ) : null}

      <ConfirmDialog
        open={confirmNoShow}
        body={t('coverage.noShowConfirmBody')}
        confirmLabel={t('coverage.markNoShow')}
        loading={complete.isPending}
        danger
        onCancel={() => setConfirmNoShow(false)}
        onConfirm={() =>
          complete.mutate(
            { applicationId: application.id, outcome: 'no_show' },
            { onSuccess: () => setConfirmNoShow(false) },
          )
        }
      />
    </section>
  );
}

/** Phase 2: ranked providers near the masjid, matched by distance + availability. */
function CandidatesSection({ request }: { request: CoverageRequest }): JSX.Element {
  const { t } = useTranslation();
  const candidates = useCandidates(request.id, true);

  return (
    <section className="stack">
      <h3>{t('match.candidates')}</h3>
      <Async
        query={candidates}
        isEmpty={(l) => l.length === 0}
        emptyMessage={t('match.noCandidates')}
      >
        {(list) => (
          <div className="stack">
            {list.map((c) => (
              <div key={c.providerId} className="card stack">
                <div className="row row--between">
                  <strong>{c.fullName}</strong>
                  <div className="wrap">
                    {c.verified ? (
                      <Badge
                        label={t('profile.verified')}
                        color="var(--color-success)"
                        icon={<Check />}
                      />
                    ) : null}
                    {c.isCertified ? (
                      <Badge
                        label={t('profile.certified')}
                        color="var(--color-accent-text)"
                        icon={<Award />}
                      />
                    ) : null}
                  </div>
                </div>
                {c.ratingCount > 0 ? <Stars value={c.ratingAvg} count={c.ratingCount} /> : null}
                <div className="wrap">
                  {c.distanceKm != null ? (
                    <span className="muted">{t('match.distanceKm', { km: c.distanceKm })}</span>
                  ) : null}
                  <span className="muted">
                    {t('match.availabilityPct', {
                      pct: Math.round(c.availabilityScore * 100),
                    })}
                  </span>
                  <span className="muted">
                    {t('profile.reliability', {
                      pct: Math.round(c.reliabilityScore * 100),
                    })}
                  </span>
                  {c.hasApplied ? (
                    <Badge label={t('match.applied')} color="var(--color-primary)" />
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </Async>
    </section>
  );
}

/** Phase 6 (P6-3, D-012): masjid-side rating is manager-only. Non-managers see
 * a read-only message; RLS enforces the same rule server-side. */
function ManagerGatedRating({ application }: { application: CoverageApplication }): JSX.Element {
  const { t } = useTranslation();
  const canRate = useCanRateAsManager(application.id, true);
  return (
    <RatingSection
      applicationId={application.id}
      rateeId={application.providerId}
      readOnlyReason={canRate.data === false ? t('ratings.managerOnly') : null}
    />
  );
}

/** Phase 7 (D-015): no-show now auto-opens a ticket for the provider — link into it. */
function DisputeTicketLink({ applicationId }: { applicationId: string }): JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useTicketByApplication(applicationId);
  if (query.isPending) return null;
  const ticket = query.data ?? null;
  if (!ticket) {
    return (
      <Button variant="outline" onClick={() => navigate('/support/new')}>
        {t('support.disputeNoShow')}
      </Button>
    );
  }
  return (
    <Button variant="outline" onClick={() => navigate(`/support/tickets/${ticket.id}`)}>
      {t('support.discussOutcome')}
    </Button>
  );
}
